import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { Search, Plus, Mail, Phone, MapPin, FileText, Receipt, Activity, Trash2, Pencil, X, Save, DollarSign, CalendarClock, RefreshCw, FileUp, Contact, FolderUp } from "lucide-react";
import { toast } from "sonner";
import { ImportCustomersDialog } from "@/components/ImportCustomersDialog";
import { ReconcileCustomersDialog } from "@/components/ReconcileCustomersDialog";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { ManualInvoiceDialog } from "@/components/ManualInvoiceDialog";
import { FirstInvoiceDialog } from "@/components/FirstInvoiceDialog";
import { InvoiceStatusDialog } from "@/components/InvoiceStatusDialog";
import { LegacyInvoiceDialog } from "@/components/LegacyInvoiceDialog";
import { ContactFillDialog } from "@/components/ContactFillDialog";
import { AccessToggleButton } from "@/components/AccessToggleButton";
import { BulkLegacyInvoiceDialog } from "@/components/BulkLegacyInvoiceDialog";

const TYPE_OPTIONS = ["residential", "residential_dumpster", "commercial", "rolloff", "property_mgmt", "municipal"];
const BILLING_FREQUENCY_OPTIONS = [
    { value: "monthly",   label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly",    label: "Yearly" },
];
const ROUTE_DAY_OPTIONS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function Customers() {
    const [customers, setCustomers] = useState([]);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [selected, setSelected] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [openNew, setOpenNew] = useState(false);
    const [openImport, setOpenImport] = useState(false);
    const [openReconcile, setOpenReconcile] = useState(false);
    const [openContacts, setOpenContacts] = useState(false);
    const [openBulkLegacy, setOpenBulkLegacy] = useState(false);
    const [legacyOpen, setLegacyOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkDay, setBulkDay] = useState("");
    const [bulkBusy, setBulkBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editDraft, setEditDraft] = useState(null);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [firstBillOpen, setFirstBillOpen] = useState(false);
    const [statusInvoice, setStatusInvoice] = useState(null); // invoice whose status is being edited

    const startEdit = () => {
        if (!selected) return;
        setEditDraft({
            name: selected.name || "",
            email: selected.email || "",
            phone: selected.phone || "",
            type: selected.type || "residential",
            line1: selected.address?.line1 || "",
            line2: selected.address?.line2 || "",
            city: selected.address?.city || "",
            state: selected.address?.state || "",
            zip: selected.address?.zip || "",
            monthly_rate: selected.monthly_rate || 0,
            service_plan: selected.service_plan || "weekly",
            billing_frequency: selected.billing_frequency || selected.billing_cycle || "quarterly",
            notes: selected.notes || "",
            cart_quantity: selected.cart_quantity || 1,
            rate_override_reason: selected.rate_override_reason || "",
        });
        setEditing(true);
    };

    const cancelEdit = () => { setEditing(false); setEditDraft(null); };

    const saveEdit = async () => {
        if (!selected || !editDraft) return;
        if (!editDraft.name.trim()) { toast.error("Name is required"); return; }
        try {
            const payload = {
                name: editDraft.name.trim(),
                email: editDraft.email.trim(),
                phone: editDraft.phone.trim(),
                type: editDraft.type,
                address: {
                    line1: editDraft.line1.trim(),
                    line2: editDraft.line2.trim(),
                    city: editDraft.city.trim(),
                    state: editDraft.state.trim(),
                    zip: editDraft.zip.trim(),
                },
                monthly_rate: parseFloat(editDraft.monthly_rate) || 0,
                service_plan: editDraft.service_plan,
                billing_frequency: editDraft.billing_frequency,
                notes: editDraft.notes,
                rate_override_reason: (editDraft.rate_override_reason || "").trim(),
            };
            const { data } = await api.patch(`/customers/${selected.id}`, payload);
            setSelected(data);
            setEditing(false);
            setEditDraft(null);
            await load();
            toast.success("Customer updated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const toggleId = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());

    const applyBulkDay = async () => {
        if (!bulkDay || selectedIds.size === 0) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/customers/bulk-route-day", {
                route_day: bulkDay,
                customer_ids: [...selectedIds],
            });
            toast.success(`Set ${data.modified} customer${data.modified === 1 ? "" : "s"} to ${titleCase(bulkDay)}`);
            clearSelection();
            setBulkDay("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk update failed");
        } finally { setBulkBusy(false); }
    };

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (typeFilter) params.type = typeFilter;
        const { data } = await api.get("/customers", { params });
        setCustomers(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, typeFilter]);

    const toggleAllVisible = () => {
        const all = customers.map((c) => c.id);
        const allSelected = all.every((id) => selectedIds.has(id));
        setSelectedIds(allSelected ? new Set() : new Set(all));
    };

    const openCustomer = async (c) => {
        setSelected(c);
        const { data } = await api.get(`/customers/${c.id}/timeline`);
        setTimeline(data);
    };

    return (
        <div data-testid="customers-page">
            <PageHeader
                title="Customer CRM"
                subtitle="Residential, commercial, roll-off, property management and municipal accounts."
                actions={
                    <div className="flex flex-wrap items-center justify-end gap-2 max-w-full">
                        <Button variant="outline" size="sm"
                                onClick={() => setOpenReconcile(true)}
                                data-testid="reconcile-customers-btn">
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Reconcile CSV
                        </Button>
                        <Button variant="outline" size="sm"
                                onClick={() => setOpenContacts(true)}
                                data-testid="fill-contacts-btn">
                            <Contact className="w-4 h-4 mr-1" />
                            Fill contacts CSV
                        </Button>
                        <ContactFillDialog open={openContacts} setOpen={setOpenContacts} onDone={load} />
                        <Button variant="outline" size="sm"
                                onClick={() => setOpenBulkLegacy(true)}
                                data-testid="bulk-legacy-btn">
                            <FolderUp className="w-4 h-4 mr-1" />
                            Bulk legacy invoices
                        </Button>
                        <BulkLegacyInvoiceDialog open={openBulkLegacy} setOpen={setOpenBulkLegacy} onDone={load} />
                        <ReconcileCustomersDialog
                            open={openReconcile}
                            setOpen={setOpenReconcile}
                            onImported={load}
                        />
                        <ImportCustomersDialog
                            open={openImport}
                            setOpen={setOpenImport}
                            onImported={load}
                        />
                        <NewCustomerSheet open={openNew} setOpen={setOpenNew} onSaved={() => { setOpenNew(false); load(); }} />
                    </div>
                }
            />
            <div className="px-8 py-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search by name, address, account #..."
                               value={q} onChange={(e) => setQ(e.target.value)}
                               className="pl-9" data-testid="customer-search-input" />
                    </div>
                    <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-48" data-testid="customer-type-filter">
                            <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="text-xs text-muted-foreground font-mono ml-auto">
                        {customers.length} accounts
                    </div>
                </div>

                <Card>
                    {selectedIds.size > 0 && (
                        <div className="flex items-center gap-3 p-3 border-b border-border bg-primary/5" data-testid="bulk-action-bar">
                            <div className="text-sm font-medium flex-1">
                                {selectedIds.size} selected
                            </div>
                            <span className="text-xs text-muted-foreground">Set route day →</span>
                            <Select value={bulkDay} onValueChange={setBulkDay}>
                                <SelectTrigger className="w-40 h-8" data-testid="bulk-route-day-select">
                                    <SelectValue placeholder="Pick a day" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROUTE_DAY_OPTIONS.map((d) => (
                                        <SelectItem key={d} value={d}>{titleCase(d)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button size="sm" onClick={applyBulkDay}
                                    disabled={!bulkDay || bulkBusy}
                                    data-testid="bulk-apply-btn">
                                {bulkBusy ? "Applying…" : "Apply"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={clearSelection}
                                    data-testid="bulk-clear-btn">
                                Clear
                            </Button>
                        </div>
                    )}
                    <div className="overflow-x-auto" data-testid="customers-table">
                        <table className="w-full text-sm">
                            <thead>
                            <tr className="border-b border-border surface">
                                <th className="px-3 py-3 w-8">
                                    <input type="checkbox"
                                           className="cursor-pointer"
                                           data-testid="bulk-select-all"
                                           checked={customers.length > 0 && customers.every((c) => selectedIds.has(c.id))}
                                           onChange={toggleAllVisible} />
                                </th>
                                <th className="text-left px-4 py-3 overline">Account</th>
                                <th className="text-left px-4 py-3 overline">Type</th>
                                <th className="text-left px-4 py-3 overline">Address</th>
                                <th className="text-left px-4 py-3 overline">Route day</th>
                                <th className="text-right px-4 py-3 overline">Quarterly</th>
                                <th className="text-right px-4 py-3 overline">Balance</th>
                                <th className="text-left px-4 py-3 overline">Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {customers.map((c) => (
                                <tr key={c.id}
                                    onClick={() => openCustomer(c)}
                                    data-testid={`customer-row-${c.id}`}
                                    className={`border-b border-border hover:bg-secondary/50 cursor-pointer ${selectedIds.has(c.id) ? "bg-primary/5" : ""}`}>
                                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox"
                                               className="cursor-pointer"
                                               data-testid={`bulk-select-${c.id}`}
                                               checked={selectedIds.has(c.id)}
                                               onChange={() => toggleId(c.id)} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{c.name}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{c.account_number}</div>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{titleCase(c.type)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.address?.line1}, {c.address?.city}</td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {c.route_day ? titleCase(c.route_day) : <span className="text-xs italic">unassigned</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular">{fmtMoney(c.monthly_rate)}</td>
                                    <td className="px-4 py-3 text-right tabular">
                                        <span className={c.balance > 0 ? "text-red-600 dark:text-red-400 font-semibold" : ""}>{fmtMoney(c.balance)}</span>
                                    </td>
                                    <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            <Sheet open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); cancelEdit(); } }}>
                <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="customer-detail-sheet">
                    {selected && (
                        <>
                            <SheetHeader>
                                <div className="overline">{selected.account_number}</div>
                                <SheetTitle className="font-heading text-2xl tracking-tight">{selected.name}</SheetTitle>
                                <SheetDescription className="sr-only">
                                    Customer account details, timeline, invoices, and roll-off jobs.
                                </SheetDescription>
                                <div className="flex flex-wrap gap-2 items-center pt-1">
                                    <StatusPill status={selected.status} />
                                    <span className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-medium">{titleCase(selected.type)}</span>
                                    {selected.autopay && <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">AutoPay</span>}
                                    {(selected.rate_override_reason || "").trim() && (
                                        <span
                                            className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium inline-flex items-center gap-1"
                                            title={`Custom rate — ${selected.rate_override_reason}`}
                                            data-testid="customer-rate-override-badge"
                                        >
                                            <DollarSign className="w-3 h-3" />
                                            Custom rate
                                        </span>
                                    )}
                                    {selected.access_suspended && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                              data-testid="customer-portal-suspended-badge">
                                            Portal suspended
                                        </span>
                                    )}
                                    <div className="ml-auto flex gap-1">
                                        {!editing && (
                                            <>
                                                <AccessToggleButton
                                                    kind="customer" id={selected.id} name={selected.name}
                                                    suspended={!!selected.access_suspended}
                                                    onChanged={(v) => {
                                                        setSelected((c) => ({ ...c, access_suspended: v }));
                                                        load();
                                                    }} />
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setFirstBillOpen(true)}
                                                    data-testid="start-billing-btn"
                                                    title={selected.next_billing_date
                                                        ? "Change first-invoice schedule"
                                                        : "Start recurring billing for this customer"}>
                                                    <CalendarClock className="w-4 h-4 mr-1" />
                                                    {selected.next_billing_date ? "Change billing" : "Start billing"}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setLegacyOpen(true)}
                                                    data-testid="legacy-invoice-btn"
                                                    title="Upload this customer's invoice PDF from the old system">
                                                    <FileUp className="w-4 h-4 mr-1" />
                                                    Legacy invoice
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setInvoiceOpen(true)}
                                                    data-testid="create-invoice-btn">
                                                    <Receipt className="w-4 h-4 mr-1" />
                                                    Invoice
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={startEdit}
                                                    data-testid="edit-customer-btn">
                                                    <Pencil className="w-4 h-4 mr-1" />
                                                    Edit
                                                </Button>
                                            </>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                            data-testid="delete-customer-btn"
                                            onClick={async () => {
                                                const label = `${selected.name} (${selected.account_number})`;
                                                if (!window.confirm(
                                                    `Delete ${label}?\n\nThis removes the customer permanently. Their invoices, timeline entries, and pending charges will remain in the database for audit purposes. This action can't be undone.`
                                                )) return;
                                                try {
                                                    await api.delete(`/customers/${selected.id}`);
                                                    toast.success(`Deleted ${label}`);
                                                    setSelected(null);
                                                    await load();
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Delete failed");
                                                }
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4 mr-1" />
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            </SheetHeader>

                            {editing ? (
                                <EditCustomerForm
                                    draft={editDraft}
                                    setDraft={setEditDraft}
                                    onSave={saveEdit}
                                    onCancel={cancelEdit}
                                />
                            ) : (
                                <div className="grid grid-cols-2 gap-4 mt-6">
                                    <InfoRow icon={Mail} label="Email" value={selected.email || "—"} />
                                    <InfoRow icon={Phone} label="Phone" value={selected.phone || "—"} />
                                    <InfoRow icon={MapPin} label="Address"
                                             value={`${selected.address?.line1}, ${selected.address?.city}, ${selected.address?.state} ${selected.address?.zip}`} />
                                    <InfoRow icon={Receipt} label="Quarterly rate" value={fmtMoney(selected.monthly_rate)} />
                                    <InfoRow icon={Activity} label="Service plan" value={titleCase(selected.service_plan)} />
                                    <InfoRow icon={FileText} label="Balance" value={fmtMoney(selected.balance)} />
                                    <RouteDayEditor
                                        customer={selected}
                                        onSaved={(patched) => { setSelected(patched); load(); }}
                                    />
                                </div>
                            )}

                            <Tabs defaultValue="timeline" className="mt-8">
                                <TabsList>
                                    <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                                    <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
                                    <TabsTrigger value="charges" data-testid="tab-charges">Extra Charges</TabsTrigger>
                                    <TabsTrigger value="photos" data-testid="tab-photos">
                                        Photos {timeline?.stop_photos?.length ? `(${timeline.stop_photos.length})` : ""}
                                    </TabsTrigger>
                                    <TabsTrigger value="rolloff" data-testid="tab-rolloff">Roll-off</TabsTrigger>
                                </TabsList>
                                <TabsContent value="timeline" className="mt-4 space-y-3">
                                    {timeline?.communications?.length ? timeline.communications.map((c) => (
                                        <div key={c.id} className="flex items-start gap-3 p-3 rounded-md border border-border">
                                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">{c.subject || c.body}</div>
                                                <div className="text-[11px] text-muted-foreground mt-1">{c.channel.toUpperCase()} · {fmtDate(c.created_at)}</div>
                                            </div>
                                        </div>
                                    )) : <div className="text-sm text-muted-foreground py-6">No timeline entries.</div>}
                                </TabsContent>
                                <TabsContent value="invoices" className="mt-4 space-y-2">
                                    {timeline?.invoices?.length ? timeline.invoices.map((inv) => (
                                        <div key={inv.id} className="flex items-center justify-between p-3 rounded-md border border-border">
                                            <div>
                                                <div className="text-sm font-mono">{inv.invoice_number}</div>
                                                <div className="text-xs text-muted-foreground">{fmtDate(inv.issued_date)} · {inv.items?.[0]?.description || ""}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {inv.legacy_pdf_file_id && (
                                                    <Button variant="ghost" size="sm"
                                                            data-testid={`inv-legacy-pdf-${inv.id}`}
                                                            onClick={() => openLegacyPdf(inv.id)}>
                                                        <FileText className="w-4 h-4 mr-1" />PDF
                                                    </Button>
                                                )}
                                                <div className="text-right">
                                                    <div className="text-sm font-semibold tabular">{fmtMoney(inv.total)}</div>
                                                    <StatusPill status={inv.status} />
                                                </div>
                                                <Button
                                                    variant="outline" size="sm"
                                                    data-testid={`inv-change-status-${inv.id}`}
                                                    onClick={() => setStatusInvoice(inv)}
                                                >
                                                    Change status
                                                </Button>
                                            </div>
                                        </div>
                                    )) : <div className="text-sm text-muted-foreground py-6">No invoices.</div>}
                                </TabsContent>
                                <TabsContent value="charges" className="mt-4">
                                    <PendingChargesTab customerId={selected.id} />
                                </TabsContent>
                                <TabsContent value="photos" className="mt-4">
                                    <CustomerPhotosTab
                                        customerId={selected.id}
                                        officePhotos={timeline?.office_photos || []}
                                        stopPhotos={timeline?.stop_photos || []}
                                        onChanged={async () => {
                                            if (selected) {
                                                const { data } = await api.get(`/customers/${selected.id}/timeline`);
                                                setTimeline(data);
                                            }
                                        }}
                                    />
                                </TabsContent>
                                <TabsContent value="rolloff" className="mt-4 space-y-2">
                                    {timeline?.rolloff_jobs?.length ? timeline.rolloff_jobs.map((j) => (
                                        <div key={j.id} className="flex items-center justify-between p-3 rounded-md border border-border">
                                            <div>
                                                <div className="text-sm font-mono">{j.job_number}</div>
                                                <div className="text-xs text-muted-foreground">{titleCase(j.job_type)} · {j.container_size} · {fmtDate(j.scheduled_date)}</div>
                                            </div>
                                            <StatusPill status={j.status} />
                                        </div>
                                    )) : <div className="text-sm text-muted-foreground py-6">No roll-off jobs.</div>}
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            <ManualInvoiceDialog
                open={invoiceOpen}
                onOpenChange={setInvoiceOpen}
                customer={selected}
                onCreated={async () => {
                    if (selected) {
                        const { data } = await api.get(`/customers/${selected.id}/timeline`);
                        setTimeline(data);
                    }
                }}
            />

            <LegacyInvoiceDialog
                open={legacyOpen}
                onOpenChange={setLegacyOpen}
                customer={selected}
                onCreated={async () => {
                    await load();
                    if (selected) {
                        const fresh = await api.get(`/customers/${selected.id}`);
                        setSelected(fresh.data);
                        const { data } = await api.get(`/customers/${selected.id}/timeline`);
                        setTimeline(data);
                    }
                }}
            />

            <FirstInvoiceDialog
                open={firstBillOpen}
                onOpenChange={setFirstBillOpen}
                customer={selected}
                onDone={async () => {
                    await load();
                    if (selected) {
                        const fresh = await api.get(`/customers/${selected.id}`);
                        setSelected(fresh.data);
                        const { data } = await api.get(`/customers/${selected.id}/timeline`);
                        setTimeline(data);
                    }
                }}
            />

            <InvoiceStatusDialog
                open={!!statusInvoice}
                onOpenChange={(v) => !v && setStatusInvoice(null)}
                invoice={statusInvoice}
                onUpdated={async () => {
                    if (selected) {
                        const fresh = await api.get(`/customers/${selected.id}`);
                        setSelected(fresh.data);
                        const { data } = await api.get(`/customers/${selected.id}/timeline`);
                        setTimeline(data);
                    }
                    // Refresh the parent customer list so any balance change
                    // shows without needing a page reload.
                    await load();
                }}
            />
        </div>
    );
}

async function openLegacyPdf(invoiceId) {
    try {
        const { data } = await api.get(`/invoices/${invoiceId}/legacy-pdf`, { responseType: "blob" });
        window.open(URL.createObjectURL(data), "_blank");
    } catch {
        toast.error("Could not open PDF");
    }
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="p-3 rounded-md border border-border surface">
            <div className="flex items-center gap-2 overline mb-1">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="text-sm font-medium">{value}</div>
        </div>
    );
}


function EditCustomerForm({ draft, setDraft, onSave, onCancel }) {
    if (!draft) return null;
    const set = (k, v) => setDraft({ ...draft, [k]: v });
    return (
        <div className="mt-6 space-y-4 border border-border rounded-lg p-4 bg-secondary/30"
             data-testid="edit-customer-form">
            <div className="flex items-center justify-between">
                <div className="font-heading font-semibold">Edit customer</div>
                <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={onCancel} data-testid="edit-cancel-btn">
                        <X className="w-4 h-4 mr-1" />Cancel
                    </Button>
                    <Button size="sm" onClick={onSave} data-testid="edit-save-btn">
                        <Save className="w-4 h-4 mr-1" />Save changes
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                    <Label className="text-xs mb-1 block">Name *</Label>
                    <Input value={draft.name} onChange={(e) => set("name", e.target.value)}
                           data-testid="edit-name-input" />
                </div>
                <div>
                    <Label className="text-xs mb-1 block">Email</Label>
                    <Input type="email" value={draft.email}
                           onChange={(e) => set("email", e.target.value)}
                           data-testid="edit-email-input" />
                </div>
                <div>
                    <Label className="text-xs mb-1 block">Phone</Label>
                    <Input value={draft.phone} placeholder="555-0100"
                           onChange={(e) => set("phone", e.target.value)}
                           data-testid="edit-phone-input" />
                </div>
                <div>
                    <Label className="text-xs mb-1 block">Type</Label>
                    <Select value={draft.type} onValueChange={(v) => set("type", v)}>
                        <SelectTrigger data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TYPE_OPTIONS.map((t) => (
                                <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs mb-1 block">Service plan</Label>
                    <Select value={draft.service_plan} onValueChange={(v) => set("service_plan", v)}>
                        <SelectTrigger data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Bi-weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="pt-2 border-t border-border">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Address</div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label className="text-xs mb-1 block">Street</Label>
                        <GoogleAddressInput
                            value={draft.line1}
                            onChange={(v) => set("line1", v)}
                            onAddressSelected={(a) => setDraft((c) => ({
                                ...c,
                                line1: a.line1 || c.line1,
                                city: a.city || c.city,
                                state: a.state || c.state,
                                zip: a.zip || c.zip,
                            }))}
                            placeholder="123 Main St"
                            testid="edit-line1-input"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs mb-1 block">Apt / Unit (optional)</Label>
                        <Input value={draft.line2}
                               onChange={(e) => set("line2", e.target.value)}
                               data-testid="edit-line2-input" />
                    </div>
                    <div>
                        <Label className="text-xs mb-1 block">City</Label>
                        <Input value={draft.city}
                               onChange={(e) => set("city", e.target.value)}
                               data-testid="edit-city-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs mb-1 block">State</Label>
                            <Input value={draft.state} maxLength={2} placeholder="TX"
                                   onChange={(e) => set("state", e.target.value.toUpperCase())}
                                   data-testid="edit-state-input" />
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">ZIP</Label>
                            <Input value={draft.zip}
                                   onChange={(e) => set("zip", e.target.value)}
                                   data-testid="edit-zip-input" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-2 border-t border-border">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs mb-1 block">Rate ($)</Label>
                        <Input type="number" step="0.01" value={draft.monthly_rate}
                               onChange={(e) => set("monthly_rate", e.target.value)}
                               data-testid="edit-rate-input" />
                        {(() => {
                            const qty = Math.max(1, Math.min(4, parseInt(draft.cart_quantity, 10) || 1));
                            const std = ({ 1: 96, 2: 135, 3: 165, 4: 200 })[qty];
                            const current = parseFloat(draft.monthly_rate) || 0;
                            const mismatch = Math.abs(current - std) > 0.5;
                            const override = (draft.rate_override_reason || "").trim().length > 0;
                            return (
                                <div className="text-[10px] mt-1 space-y-0.5">
                                    <div className="text-muted-foreground">
                                        Whatever amount is charged each billing cycle (not divided by cycle length).
                                    </div>
                                    <div className={mismatch && !override ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}>
                                        Standard {qty}-cart quarterly rate: ${std}
                                        {mismatch && !override && " — add a reason below to mark this as a negotiated override."}
                                        {mismatch && override && " — override active."}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    <div>
                        <Label className="text-xs mb-1 block">Billing frequency</Label>
                        <Select value={draft.billing_frequency}
                                onValueChange={(v) => set("billing_frequency", v)}>
                            <SelectTrigger data-testid="edit-billing-frequency-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {BILLING_FREQUENCY_OPTIONS.map((f) => (
                                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            Monthly for dumpsters, Quarterly for most residential, Yearly for pre-pay accounts.
                        </div>
                    </div>
                </div>
                <div className="mt-3">
                    <Label className="text-xs mb-1 block">Rate override reason (optional)</Label>
                    <Input
                        value={draft.rate_override_reason || ""}
                        onChange={(e) => set("rate_override_reason", e.target.value)}
                        placeholder="e.g. Grandfathered legacy rate, long-time customer discount, package deal"
                        data-testid="edit-rate-override-input"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1">
                        Fill in when this customer's rate is intentionally different from the standard cart tier — shows a "Custom rate" badge and skips the audit warning.
                    </div>
                </div>
            </div>

            <div>
                <Label className="text-xs mb-1 block">Notes</Label>
                <textarea
                    value={draft.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    className="w-full min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="edit-notes-input"
                />
            </div>
        </div>
    );
}


function RouteDayEditor({ customer, onSaved }) {
    const [saving, setSaving] = useState(false);
    const current = customer.route_day || "";
    const save = async (v) => {
        if (v === current) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/customers/${customer.id}`, { route_day: v });
            toast.success(`Route day set to ${titleCase(v)}`);
            onSaved?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally { setSaving(false); }
    };
    return (
        <div className="p-3 rounded-md border border-border surface" data-testid="customer-route-day-editor">
            <div className="flex items-center gap-2 overline mb-1">
                <Activity className="w-3 h-3" /> Route day
            </div>
            <Select value={current} onValueChange={save} disabled={saving}>
                <SelectTrigger className="h-8 text-sm" data-testid="customer-route-day-select">
                    <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                    {ROUTE_DAY_OPTIONS.map((d) => (
                        <SelectItem key={d} value={d} data-testid={`route-day-option-${d}`}>{titleCase(d)}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function PendingChargesTab({ customerId }) {
    const [charges, setCharges] = useState([]);
    const [desc, setDesc] = useState("");
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get(`/customers/${customerId}/pending-charges`);
        setCharges(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

    const add = async () => {
        if (!desc.trim() || !parseFloat(amount)) {
            toast.error("Description and amount required");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/customers/${customerId}/pending-charges`, {
                description: desc.trim(), amount: parseFloat(amount),
            });
            toast.success("Charge added — it'll be on the next invoice");
            setDesc(""); setAmount(""); await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Add failed");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this pending charge?")) return;
        try {
            await api.delete(`/pending-charges/${id}`);
            await load();
            toast.success("Removed");
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const total = charges.reduce((s, c) => s + c.amount, 0);

    return (
        <div className="space-y-3" data-testid="pending-charges-tab">
            <div className="text-xs text-muted-foreground">
                Fees that roll into the next quarterly invoice — extra bags, bulk pickup, contamination, etc.
            </div>
            <div className="flex gap-2 items-end">
                <div className="flex-1">
                    <Label className="text-xs mb-1 block">Description</Label>
                    <Input value={desc} onChange={(e) => setDesc(e.target.value)}
                           placeholder="Extra bag" data-testid="pending-charge-desc" />
                </div>
                <div className="w-28">
                    <Label className="text-xs mb-1 block">Amount</Label>
                    <Input type="number" step="0.01" value={amount}
                           onChange={(e) => setAmount(e.target.value)}
                           placeholder="5.00" data-testid="pending-charge-amount" />
                </div>
                <Button onClick={add} disabled={busy} data-testid="add-pending-charge-btn">
                    <Plus className="w-4 h-4 mr-1" />Add
                </Button>
            </div>
            <div className="space-y-2">
                {charges.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6">
                        No pending charges. Add extras above and they'll appear on the next quarterly invoice.
                    </div>
                ) : charges.map((c) => (
                    <div key={c.id}
                         className="flex items-center justify-between p-3 rounded-md border border-border"
                         data-testid={`pending-charge-${c.id}`}>
                        <div>
                            <div className="text-sm font-medium">{c.description}</div>
                            <div className="text-xs text-muted-foreground">
                                Added by {c.created_by_name} ({c.created_by_role}) · {fmtDate(c.created_at)}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="tabular font-semibold">{fmtMoney(c.amount)}</div>
                            <Button size="sm" variant="ghost" onClick={() => remove(c.id)}
                                    data-testid={`delete-pending-charge-${c.id}`}>×</Button>
                        </div>
                    </div>
                ))}
                {charges.length > 0 && (
                    <div className="flex justify-end pt-2 border-t border-border text-sm">
                        <span className="text-muted-foreground mr-3">Total on next invoice:</span>
                        <span className="tabular font-semibold">{fmtMoney(total)}</span>
                    </div>
                )}
            </div>
        </div>
    );
}


function NewCustomerSheet({ open, setOpen, onSaved }) {
    const [form, setForm] = useState({
        name: "", type: "residential", email: "", phone: "",
        line1: "", city: "", state: "CT", zip: "", monthly_rate: 96,
        route_day: "monday",
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        try {
            await api.post("/customers", {
                name: form.name, type: form.type, email: form.email, phone: form.phone,
                monthly_rate: parseFloat(form.monthly_rate || 0),
                address: { line1: form.line1, city: form.city, state: form.state, zip: form.zip },
                route_day: form.route_day,
            });
            toast.success("Customer created");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button size="sm" data-testid="new-customer-btn"><Plus className="w-4 h-4 mr-1" /> New customer</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="new-customer-sheet">
                <SheetHeader>
                    <SheetTitle className="font-heading text-2xl">New customer</SheetTitle>
                    <SheetDescription className="sr-only">Create a new customer record.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-3">
                    <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-customer-name" /></Field>
                    <Field label="Type">
                        <Select value={form.type} onValueChange={(v) => set("type", v)}>
                            <SelectTrigger data-testid="new-customer-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
                        <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                    </div>
                    <Field label="Address">
                        <GoogleAddressInput
                            value={form.line1}
                            onChange={(v) => set("line1", v)}
                            onAddressSelected={(a) => setForm((c) => ({
                                ...c,
                                line1: a.line1 || c.line1,
                                city: a.city || c.city,
                                state: a.state || c.state,
                                zip: a.zip || c.zip,
                            }))}
                            placeholder="Start typing an address..."
                            testid="new-customer-address"
                        />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
                        <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
                        <Field label="Zip"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
                    </div>
                    <Field label="Quarterly rate">
                        <Input type="number" value={form.monthly_rate}
                               onChange={(e) => set("monthly_rate", e.target.value)}
                               placeholder="96 = 1 cart · 135 = 2 carts · 165 = 3 · 200 = 4" />
                    </Field>
                    <Field label="Pickup route day">
                        <Select value={form.route_day} onValueChange={(v) => set("route_day", v)}>
                            <SelectTrigger data-testid="new-customer-route-day"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ROUTE_DAY_OPTIONS.map((d) => <SelectItem key={d} value={d}>{titleCase(d)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Button onClick={submit} className="w-full mt-4" data-testid="new-customer-submit">Create customer</Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}


/** Photos tab on the customer detail sheet — two sections:
 *  1) Office uploads (attached from admin console; can be deleted)
 *  2) Route photos (auto-collected from every driver stop photo)
 */
function CustomerPhotosTab({ customerId, officePhotos, stopPhotos, onChanged }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onFilesPicked = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data: up } = await api.post("/uploads?kind=customer-photo", fd);
                await api.post(`/customers/${customerId}/photos`, { file_id: up.id });
            }
            toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} uploaded`);
            await onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            try { e.target.value = ""; } catch { /* readonly on some UAs */ }
        }
    };

    const deletePhoto = async (photo) => {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await api.delete(`/customers/${customerId}/photos/${photo.id}`);
            toast.success("Photo deleted");
            await onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete");
        }
    };

    return (
        <div className="space-y-6" data-testid="customer-photos-tab">
            {/* Upload zone */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="overline">Office uploads {officePhotos.length > 0 && `· ${officePhotos.length}`}</div>
                    <Button
                        variant="outline" size="sm"
                        data-testid="customer-photo-upload-btn"
                        disabled={uploading}
                        onClick={() => inputRef.current?.click()}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        {uploading ? "Uploading…" : "Add photos"}
                    </Button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onFilesPicked}
                        data-testid="customer-photo-file-input"
                    />
                </div>
                {officePhotos.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="customer-office-photos-grid">
                        {officePhotos.map((p) => (
                            <div key={p.id} className="relative group border border-border rounded-md overflow-hidden hover:border-primary transition-colors">
                                <a href={p.photo_url} target="_blank" rel="noreferrer" className="block">
                                    <img
                                        src={p.photo_url}
                                        alt={p.note || "customer photo"}
                                        className="w-full aspect-square object-cover"
                                    />
                                </a>
                                <button
                                    type="button"
                                    onClick={() => deletePhoto(p)}
                                    data-testid={`customer-photo-delete-${p.id}`}
                                    aria-label="Delete photo"
                                    className="absolute top-1 right-1 bg-background/95 rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                </button>
                                <div className="p-2 text-xs">
                                    {p.note && <div className="font-medium truncate">{p.note}</div>}
                                    <div className="text-muted-foreground truncate">
                                        {p.uploaded_by_name || p.uploaded_by_email} · {p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="border border-dashed border-border rounded-md p-6 text-center text-xs text-muted-foreground">
                        No office photos yet. Tap <b>Add photos</b> to attach cart placement,
                        damage evidence, before/after site pictures, etc.
                    </div>
                )}
            </div>

            {/* Route photos (driver-captured) */}
            <div>
                <div className="overline mb-2">
                    Route photos {stopPhotos.length > 0 && `· ${stopPhotos.length}`}
                </div>
                {stopPhotos.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="customer-photos-grid">
                        {stopPhotos.map((p) => (
                            <a key={p.stop_id + p.photo_url}
                               href={p.photo_url} target="_blank" rel="noreferrer"
                               className="group block border border-border rounded-md overflow-hidden hover:border-primary transition-colors"
                               data-testid={`customer-photo-${p.stop_id}`}>
                                <img src={p.photo_url}
                                     alt={`${p.route_name} — ${p.day}`}
                                     className="w-full aspect-square object-cover" />
                                <div className="p-2 text-xs">
                                    <div className="font-medium truncate">{p.route_name}</div>
                                    <div className="text-muted-foreground">
                                        {p.completed_at ? new Date(p.completed_at).toLocaleString() : titleCase(p.day || "")}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-muted-foreground py-4">
                        When a driver takes a photo while completing a stop, it'll appear here.
                    </div>
                )}
            </div>
        </div>
    );
}
